import React, { useEffect, useRef, useState } from 'react';
import Robot from '../../models/robots/robot';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getDailyCleanedAreaById } from '../../store/robots/robots-actions';
import { SelectChangeEvent, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { dailyCleanedAreaColorRules, DayArea } from '../../models/robots/daily-cleaned-area';
import Month from '@common/utils/month';
import { dateToStringFormat } from '@common/utils/date';
import SelectInput from '../forms/SelectInput';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import CustomLoader from '@common/components/Feedback/CustomLoader';
import { DateTime } from 'luxon';
import { useSharedSelector } from '@common/store/SharedProvider';
import { selectDistanceUnit } from '@common/store/app-configurations/app-configurations-selectors';
import DistanceUnitSet from '@common/components/misc/DistanceUnitSet';
import { DistanceUnit, DistanceValueUnit } from '@common/enums/configuration/units';
import useDistanceUnitValue from '@common/hooks/useDistanceUnitValue';
import { Theme } from '@mui/material/styles';

const RobotCleanedAreaHistory: React.FC<{ robot: Robot }> = ({ robot }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [filterParams, setFilterParams] = useSearchParams();
  const queryYear = filterParams.get(FilterKeys.Year);

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const distanceUnit = useSharedSelector((state) => selectDistanceUnit(state));
  const { multiplier } = useDistanceUnitValue(0, DistanceValueUnit.METERS_SQUARED);

  const [year, setYear] = useState(
    queryYear != null ? parseInt(queryYear, 10) : DateTime.now().year,
  );
  const [openTooltipDate, setOpenTooltipDate] = useState<string | null>(null);
  const [openLegendRuleIndex, setOpenLegendRuleIndex] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const matchesLgDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    if (robotsData.rest.getDailyCleanedAreaStatus === HttpFetchStatus.Idle) {
      dispatch(getDailyCleanedAreaById({ id: robot.id, year: year }));
    }
  }, [dispatch, robot.id, robotsData.rest.getDailyCleanedAreaStatus, year]);

  const distanceUnitLabel =
    distanceUnit === DistanceUnit.METRIC
      ? t('label-meters-squared-unit')
      : t('label-feet-squared-unit');

  const getColorForValue = (cleanedArea: number): string => {
    const displayArea = cleanedArea * multiplier;
    for (const rule of dailyCleanedAreaColorRules.slice().reverse()) {
      if (
        displayArea >=
        (distanceUnit === DistanceUnit.METRIC ? rule.fromValueMetric : rule.fromValueImperial)
      ) {
        return rule.color;
      }
    }
    return dailyCleanedAreaColorRules[0].color;
  };

  const getDaysByWeekday = (days: DayArea[]) => {
    const weekdays: { [key: number]: DayArea[] } = {
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
      7: [],
    };

    for (let monthIndex = 1; monthIndex <= 12; monthIndex++) {
      // Fill initial blanks
      let dayChecked = 1;
      const startingWeekday = DateTime.fromObject({
        year,
        month: monthIndex,
        day: dayChecked,
        hour: 0,
        minute: 0,
      }).weekday;
      for (let weekday = 1; weekday < startingWeekday; weekday++) {
        weekdays[weekday].push({ date: '', cleanedArea: 0 });
      }

      for (let day = 1; day <= 31; day++) {
        const date = DateTime.fromObject(
          { year, month: monthIndex, day, hour: 0, minute: 0 },
          { zone: 'utc' },
        );
        if (date.month !== monthIndex) break; // Ignore invalid dates

        const dayData = days.find(
          (d) =>
            DateTime.fromISO(d.date).month === date.month &&
            DateTime.fromISO(d.date).day === date.day,
        );
        const cleanedArea = dayData ? dayData.cleanedArea : 0;
        const weekday = date.weekday;

        weekdays[weekday].push({ date: date.toISO()!, cleanedArea });
        dayChecked = day;
      }

      // Fill final blanks
      const endingWeekday = DateTime.fromObject({
        year,
        month: monthIndex,
        day: dayChecked,
        hour: 0,
        minute: 0,
      }).weekday;
      for (let weekday = endingWeekday === 7 ? 1 : endingWeekday + 1; weekday < 8; weekday++) {
        weekdays[weekday].push({ date: '', cleanedArea: 0 });
      }
    }

    return weekdays;
  };

  const yearChangeHandler = (event: SelectChangeEvent<unknown>) => {
    const newYear = event.target.value as number;
    setYear(newYear);
    dispatch(getDailyCleanedAreaById({ id: robot.id, year: newYear }));
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.Year, newYear.toString());
      return prevState;
    });
  };

  const daysByWeekday: { [key: number]: DayArea[] } =
    robotsData.currentRobotData?.dailyCleanedArea != null
      ? getDaysByWeekday(robotsData.currentRobotData.dailyCleanedArea.days)
      : {};

  const robotDailyMissionsClickHandler = (day: DayArea) => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.StartDate, day.date);
      prevState.set(FilterKeys.EndDate, day.date);
      return prevState;
    });
  };

  const dayBoxClickHandler = (day: DayArea) => {
    setOpenTooltipDate(day.date);
    if (day.cleanedArea > 0) {
      robotDailyMissionsClickHandler(day);
    }
  };

  useEffect(() => {
    if (!matchesLgDown) return;

    const container = scrollContainerRef.current;
    if (container == null || robotsData.currentRobotData?.dailyCleanedArea == null) return;

    const now = DateTime.now();
    const isCurrentYear = year === now.year;
    const daysInYear = DateTime.fromObject({ year }).daysInYear;

    const targetOrdinal = isCurrentYear ? (now.startOf('month').ordinal + now.endOf('month').ordinal) / 2 : daysInYear;
    const targetFraction = targetOrdinal / daysInYear;
    const targetScrollLeft = targetFraction * container.scrollWidth - container.clientWidth / 2;

    container.scrollLeft = Math.max(0, targetScrollLeft);
  }, [matchesLgDown, robotsData.currentRobotData?.dailyCleanedArea, year]);

  return (
    <React.Fragment>
      <Box>
        {robotsData.currentRobotData?.dailyCleanedArea != null && (
          <Box sx={{ width: 'min-content' }}>
            <SelectInput onChange={yearChangeHandler} value={year} size="small">
              {!robotsData.currentRobotData.dailyCleanedArea.years.includes(
                DateTime.now().year,
              ) && (
                  <MenuItem key={DateTime.now().year} value={DateTime.now().year}>
                    {DateTime.now().year}
                  </MenuItem>
                )}
              {robotsData.currentRobotData.dailyCleanedArea.years.map((yearOption) => (
                <MenuItem key={yearOption} value={yearOption}>
                  {yearOption}
                </MenuItem>
              ))}
            </SelectInput>
          </Box>
        )}
        <Box
          ref={scrollContainerRef}
          data-testid="cleaned-area-scroll-container"
          sx={{ overflowX: 'auto', padding: 2 }}
        >
          <Box width={robotsData.currentRobotData?.dailyCleanedArea != null ? '1500px' : undefined}>
            {robotsData.currentRobotData?.dailyCleanedArea != null ? (
              <ClickAwayListener onClickAway={() => setOpenTooltipDate(null)}>
                <Grid container flexDirection="row" width={'100%'}>
                  {Object.keys(daysByWeekday).map((_, weekdayIndex) => (
                    <Grid
                      item
                      container
                      sx={{ flexDirection: 'row', width: '100%' }}
                      key={weekdayIndex}
                    >
                      {daysByWeekday[weekdayIndex + 1].map((day, dayIndex) => (
                        <Tooltip
                          data-testid="cleaned-box-tooltip"
                          open={day.date.length > 0 && openTooltipDate === day.date}
                          slotProps={{
                            tooltip: {
                              sx: {
                                background: 'transparent',
                              },
                            },
                          }}
                          title={
                            day.date.length > 0 ? (
                              <Card sx={{ paddingX: 2, paddingY: 1 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <HeatBox
                                    width={theme.spacing(2)}
                                    color={getColorForValue(day.cleanedArea)}
                                  />
                                  <Typography>
                                    {dateToStringFormat(DateTime.fromISO(day.date), true)}
                                  </Typography>
                                  <Typography>
                                    <DistanceUnitSet
                                      value={day.cleanedArea}
                                      valueUnit={DistanceValueUnit.METERS_SQUARED}
                                      round
                                    />
                                  </Typography>
                                </Stack>
                              </Card>
                            ) : undefined
                          }
                          placement="top"
                          key={dayIndex}
                        >
                          {day.date.length > 0 ? (
                            <ButtonBase
                              data-testid="cleaned-day-box"
                              aria-label={`${dateToStringFormat(
                                DateTime.fromISO(day.date),
                                true,
                              )}, ${Math.round(day.cleanedArea * multiplier)} ${distanceUnitLabel}`}
                              sx={(theme) => ({
                                aspectRatio: '1',
                                bgcolor: getColorForValue(day.cleanedArea),
                                borderRadius: theme.spacing(0.5),
                                margin: theme.spacing(0.2),
                                minWidth: { sm: theme.spacing(2), md: 'min-content' },
                                width: `calc((100% /  ${daysByWeekday[1].length}) - ${theme.spacing(0.4)})`,
                                cursor: day.cleanedArea > 0 ? 'pointer' : 'default',
                              })}
                              onMouseEnter={() => setOpenTooltipDate(day.date)}
                              onMouseLeave={() => setOpenTooltipDate(null)}
                              onFocusVisible={() => setOpenTooltipDate(day.date)}
                              onBlur={() => setOpenTooltipDate(null)}
                              onClick={() => dayBoxClickHandler(day)}
                            />
                          ) : (
                            <Box
                              data-testid="filler-box"
                              aria-hidden
                              sx={(theme) => ({
                                aspectRatio: '1',
                                borderRadius: theme.spacing(0.5),
                                margin: theme.spacing(0.2),
                                minWidth: { sm: theme.spacing(2), md: 'min-content' },
                                width: `calc((100% /  ${daysByWeekday[1].length}) - ${theme.spacing(0.4)})`,
                              })}
                            />
                          )}
                        </Tooltip>
                      ))}
                    </Grid>
                  ))}
                  <Grid
                    item
                    container
                    sx={{
                      flexDirection: 'row',
                      width: '100%',
                      mt: 1,
                      ml: 0,
                    }}
                  >
                    {Object.values(Month).map((month) => (
                      <Grid
                        item
                        xs={1}
                        key={month}
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-around"
                      >
                        <Stack justifyContent="space-around" height="100%">
                          <Typography textAlign="center" variant="h3">
                            {t('month-short-' + month)}
                          </Typography>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </ClickAwayListener>
            ) : (
              <CustomLoader />
            )}

            {!matchesLgDown &&
              <Stack
                direction="row"
                spacing={2}
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                width="100%"
              >
                <Typography>
                  {distanceUnit === DistanceUnit.METRIC
                    ? t('label-meters-squared-unit')
                    : t('label-feet-squared-unit')}
                </Typography>
                {dailyCleanedAreaColorRules.map((rule) => (
                  <Stack
                    direction="row"
                    display="inline-flex"
                    key={
                      distanceUnit === DistanceUnit.METRIC
                        ? rule.fromValueMetric
                        : rule.fromValueImperial
                    }
                    alignItems="center"
                    spacing={1}
                    py={1}
                    flexGrow={1}
                  >
                    <HeatBox color={rule.color} width={theme.spacing(3)} />
                    <Typography>
                      {distanceUnit === DistanceUnit.METRIC ? rule.titleMetric : rule.titleImperial}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            }
          </Box>
        </Box>
        {matchesLgDown &&
          <ClickAwayListener onClickAway={() => setOpenLegendRuleIndex(null)}>
            <Stack
              direction="row"
              spacing={0.75}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              px={2}
              py={1}
            >
              <Typography variant="caption" color="text.secondary">
                {`0 ${distanceUnit === DistanceUnit.METRIC
                  ? t('label-meters-squared-unit')
                  : t('label-feet-squared-unit')
                  }`}
              </Typography>
              {dailyCleanedAreaColorRules.map((rule, ruleIndex) => (
                <Tooltip
                  key={
                    distanceUnit === DistanceUnit.METRIC
                      ? rule.fromValueMetric
                      : rule.fromValueImperial
                  }
                  data-testid="legend-swatch-tooltip"
                  open={openLegendRuleIndex === ruleIndex}
                  slotProps={{
                    tooltip: {
                      sx: {
                        background: 'transparent',
                      },
                    },
                  }}
                  title={
                    <Card sx={{ paddingX: 2, paddingY: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>
                          {distanceUnit === DistanceUnit.METRIC
                            ? rule.titleMetric
                            : rule.titleImperial}
                        </Typography>
                        <Typography>
                          {distanceUnit === DistanceUnit.METRIC
                            ? t('label-meters-squared-unit')
                            : t('label-feet-squared-unit')}
                        </Typography>
                      </Stack>
                    </Card>
                  }
                  placement="top"
                >
                  <ButtonBase
                    data-testid="legend-swatch"
                    aria-label={`${
                      distanceUnit === DistanceUnit.METRIC ? rule.titleMetric : rule.titleImperial
                    } ${distanceUnitLabel}`}
                    onMouseEnter={() => setOpenLegendRuleIndex(ruleIndex)}
                    onMouseLeave={() => setOpenLegendRuleIndex(null)}
                    onFocusVisible={() => setOpenLegendRuleIndex(ruleIndex)}
                    onBlur={() => setOpenLegendRuleIndex(null)}
                    onClick={() => setOpenLegendRuleIndex(ruleIndex)}
                    sx={{ cursor: 'pointer', borderRadius: theme.spacing(0.5) }}
                  >
                    <HeatBox color={rule.color} width={theme.spacing(2.5)} />
                  </ButtonBase>
                </Tooltip>
              ))}
              <Typography variant="caption" color="text.secondary">
                {`${distanceUnit === DistanceUnit.METRIC
                  ? dailyCleanedAreaColorRules[dailyCleanedAreaColorRules.length - 1].titleMetric
                  : dailyCleanedAreaColorRules[dailyCleanedAreaColorRules.length - 1]
                    .titleImperial
                  } ${distanceUnit === DistanceUnit.METRIC
                    ? t('label-meters-squared-unit')
                    : t('label-feet-squared-unit')
                  }`}
              </Typography>
            </Stack>
          </ClickAwayListener>
        }
      </Box>
    </React.Fragment>
  );
};

export default RobotCleanedAreaHistory;

const HeatBox: React.FC<{ color: string | null; width: string }> = ({ color, width }) => {
  return (
    <Box
      sx={(theme) => ({
        width: width,
        aspectRatio: '1',
        bgcolor: color,
        borderRadius: theme.spacing(0.5),
        margin: theme.spacing(0.2),
      })}
    />
  );
};
